import type { ReactNode } from 'react'

import { CompanyMonogram, CompanySpine, DueDateBadge, StatusPill } from '@/shared/components/common'
import { UserAvatar } from '@/shared/components/common'
import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'
import type { Task } from '@/shared/types/domain'

import { DoneToggle } from './DoneToggle'

export interface TaskCardProps {
  task: Task
  /**
   * Sin acciones. Es lo que usa el portal y cualquier vista de consulta: en
   * `readOnly` la tarjeta no monta NI el toggle de completar ni el boton que
   * abre el detalle, no los deshabilita.
   */
  readOnly?: boolean
  /** Mostrar empresa y proyecto. En `/due-soon` es imprescindible. */
  showProject?: boolean
  onOpen?: (task: Task) => void
  onToggleDone?: (task: Task, done: boolean) => void
  /** El handle de arrastre, cuando la tarjeta vive en un tablero. */
  handle?: ReactNode
  now?: Date
  className?: string
}

export function TaskCard({
  task,
  readOnly = false,
  showProject = true,
  onOpen,
  onToggleDone,
  handle,
  now,
  className,
}: TaskCardProps) {
  const t = useT()
  const done = task.completed_at !== null

  return (
    <article
      data-testid="task-card"
      className={cn(
        'hairline flex items-stretch gap-2 rounded-[var(--radius-card)] bg-surface-1 p-2',
        className,
      )}
    >
      <CompanySpine hue={task.project.company.color_hue} label={task.project.company.name} />

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {/* Lo primero que se lee es el estado, no el nombre. */}
        <div className="flex items-center gap-2">
          {handle}
          <DueDateBadge date={task.due_date} isDone={done} now={now} />
          <StatusPill category={task.stage.category}>{task.stage.name}</StatusPill>
          <span className="flex-1" />
          <UserAvatar user={task.assignee} />
        </div>

        {readOnly ? (
          <p className={cn('truncate text-xs', done ? 'text-text-3 line-through' : 'text-text-1')}>
            {task.title}
          </p>
        ) : (
          <button
            type="button"
            onClick={() => onOpen?.(task)}
            className={cn(
              'truncate text-left text-xs hover:text-signal',
              done ? 'text-text-3 line-through' : 'text-text-1',
            )}
          >
            {task.title}
          </button>
        )}

        {showProject ? (
          <div className="flex min-w-0 items-center gap-1.5 text-2xs text-text-3">
            <CompanyMonogram
              name={task.project.company.name}
              hue={task.project.company.color_hue}
            />
            <span className="truncate">{task.project.company.name}</span>
            <span aria-hidden="true">·</span>
            <span className="truncate">{task.project.name}</span>
            {task.project.is_ongoing ? (
              <StatusPill tone="ongoing">{t('tasks.card.ongoing')}</StatusPill>
            ) : null}
          </div>
        ) : null}
      </div>

      {readOnly ? null : (
        <DoneToggle
          done={done}
          title={task.title}
          onChange={(next) => onToggleDone?.(task, next)}
          className="mt-0.5 self-start"
        />
      )}
    </article>
  )
}
